import { useMemo, useRef, useState, type ReactNode } from 'react'
import { Icon } from '../ui/Icon'
import { Sheet } from '../ui/Sheet'
import { CATEGORIES, QUICK, searchEmoji } from './emojiData'
import { readRecent, rememberPick } from './recentEmoji'
import type { Reaction } from './useReactions'

/**
 * REACTIONS UNDER A MESSAGE — owner, 2026-10-17, with a Slack screenshot: 現在我們開始來做 Slack 這種
 * 使用 emoji 來回覆某一個信息的表情包.
 *
 * Slack's shape: a chip per emoji with how many put it there, the viewer's own drawn in the accent
 * hue (a highlight is a different HUE, never a near grey — owner, 2026-10-12), a tap putting yours on
 * or taking it off, and an Add reaction button at the end of the row. The button is a VISIBLE door on
 * every message, because a feature behind a hover or a hold is one a phone never finds (owner,
 * 2026-10-14) — and it sits at the BOTTOM of the message, alone until there are chips to end
 * (owner, 2026-10-17: 應該要放在一個 message block 的最下方, 而不是放在 Agent 它的名稱的下方 — in
 * the head line it wrapped under a long name).
 *
 * WHO put each one: on a pointer, the chip's tooltip; on a phone, the Add reaction sheet lists it.
 */

export type NameOf = (user: string) => string

/** "You, Alice and Bob" — the viewer first, as Slack says it. */
export function whoReacted(users: string[], self: string, nameOf: NameOf): string {
  const names = [...users].sort((a, b) => Number(b === self) - Number(a === self)).map((u) => (u === self ? 'You' : nameOf(u)))
  if (names.length <= 1) return names[0] ?? ''
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

function AddReactionButton({ onClick, className = '' }: { onClick: () => void; className?: string }) {
  return (
    <button type="button" className={`rx-add ${className}`.trim()} aria-label="Add reaction" title="Add reaction" onClick={onClick}>
      <Icon name="add_reaction" size={18} />
    </button>
  )
}

/** How long a press must last to be a hold — the app's one long-press length (files/useCtxMenu.ts). */
const HOLD_MS = 500
/** A press that wanders farther than this is a scroll, not a hold. */
const HOLD_SLOP = 8

export function ReactionChips({
  reactions,
  self,
  nameOf,
  onToggle,
  onAdd,
  extra,
}: {
  reactions: Reaction[]
  self: string
  nameOf: NameOf
  onToggle: (emoji: string) => void
  onAdd: () => void
  /** At the end of the row, after the door: the message's bookmark (chat/RoomBookmark.tsx). */
  extra?: ReactNode
}) {
  /*
   * WHO PUT EACH ONE, on a phone — owner, 2026-10-17 (asked how a phone shows it, answered 「ok」 to
   * Slack's way): HOLD a chip and the Reactions sheet lists the people. A pointer gets the same sheet
   * from a right-click, besides the tooltip. The click that ends a hold is swallowed, so a hold never
   * ALSO puts yours on or takes it off; a press that wanders is a scroll and opens nothing.
   */
  const [whoFor, setWhoFor] = useState<string | null>(null)
  const hold = useRef<number | null>(null)
  const held = useRef(false)
  const start = useRef({ x: 0, y: 0 })
  const cancel = () => {
    if (hold.current) window.clearTimeout(hold.current)
    hold.current = null
  }
  return (
    <div className={`pmsg-rx${reactions.length === 0 ? ' empty' : ''}`} role="group" aria-label="Reactions">
      {reactions.map((r) => {
        const mine = r.users.includes(self)
        const who = whoReacted(r.users, self, nameOf)
        return (
          <button
            key={r.emoji}
            type="button"
            className={`rx-chip${mine ? ' mine' : ''}`}
            aria-pressed={mine}
            aria-label={`${r.emoji} ${r.users.length}: ${who}. ${mine ? 'Remove yours' : 'Add yours'}`}
            title={`${who} reacted with ${r.emoji}`}
            onPointerDown={(e) => {
              held.current = false
              start.current = { x: e.clientX, y: e.clientY }
              cancel()
              hold.current = window.setTimeout(() => {
                hold.current = null
                held.current = true
                setWhoFor(r.emoji)
              }, HOLD_MS)
            }}
            onPointerMove={(e) => {
              if (Math.hypot(e.clientX - start.current.x, e.clientY - start.current.y) > HOLD_SLOP) cancel()
            }}
            onPointerUp={cancel}
            onPointerLeave={cancel}
            onPointerCancel={cancel}
            /*
             * The finger lifts AFTER the sheet opened over the chip, and a browser follows a touch with
             * compatibility mouse events and a click at that point — on the sheet's scrim, which reads
             * them as a tap outside and closes the sheet the instant it appeared (measured: Chrome's
             * touch emulation, 2026-10-17). Cancelling the touchend of a hold stops all of them.
             */
            onTouchEnd={(e) => {
              if (held.current) e.preventDefault()
            }}
            onContextMenu={(e) => {
              // A right-click is followed by no click, so there is nothing to swallow here.
              e.preventDefault()
              cancel()
              setWhoFor(r.emoji)
            }}
            onClick={() => {
              if (held.current) {
                held.current = false
                return
              }
              onToggle(r.emoji)
            }}
          >
            <span className="rx-e">{r.emoji}</span>
            <span className="rx-n">{r.users.length}</span>
          </button>
        )
      })}
      <AddReactionButton onClick={onAdd} />
      {extra}
      {whoFor !== null && (
        <ReactionsWho
          reactions={reactions}
          self={self}
          nameOf={nameOf}
          first={whoFor}
          onClose={() => {
            // A hold whose click the touchend cancelled leaves nothing to swallow: the next tap — or
            // Enter on a focused chip, which has no press before it — must toggle.
            held.current = false
            setWhoFor(null)
          }}
        />
      )}
    </div>
  )
}

/**
 * The Reactions sheet — Slack's: a row of the message's emoji with their counts on top, and under it the
 * people who put the one picked (the held chip's to begin with), the viewer named "You" and first.
 */
function ReactionsWho({
  reactions,
  self,
  nameOf,
  first,
  onClose,
}: {
  reactions: Reaction[]
  self: string
  nameOf: NameOf
  first: string
  onClose: () => void
}) {
  const [shown, setShown] = useState(first)
  const r = reactions.find((x) => x.emoji === shown) ?? reactions[0]
  const people = r ? [...r.users].sort((a, b) => Number(b === self) - Number(a === self)) : []
  return (
    <Sheet title="Reactions" docked className="rx-who-sheet" onClose={onClose}>
      <div className="rx-who-tabs" role="tablist" aria-label="Reactions on this message">
        {reactions.map((x) => (
          <button
            key={x.emoji}
            type="button"
            role="tab"
            aria-selected={x.emoji === r?.emoji}
            className={`rx-who-tab${x.emoji === r?.emoji ? ' on' : ''}`}
            onClick={() => setShown(x.emoji)}
          >
            <span className="rx-e">{x.emoji}</span>
            <span className="rx-n">{x.users.length}</span>
          </button>
        ))}
      </div>
      <ul className="rx-who-list" aria-label={r ? `Reacted with ${r.emoji}` : 'Reacted'}>
        {people.map((u) => (
          <li key={u}>{u === self ? 'You' : nameOf(u)}</li>
        ))}
      </ul>
    </Sheet>
  )
}

/**
 * The picker, Slack's way — owner, 2026-10-17 (with Slack's sheet): 「搜尋框放在最上面, 下面都是其他的
 * emoji」 and 「讓它黏在底部」. A sheet docked to the bottom edge: the search box is its title row, the
 * emoji fill everything under it — Frequently Used first (this person's own recent picks, then the
 * common ones), then each kind — and a row of kinds at the foot jumps between them.
 *
 * Picking an emoji the viewer has already put on the message takes it back — the same as tapping its
 * chip, as in Slack.
 */
export function ReactionPicker({
  reactions,
  self,
  onPick,
  onClose,
}: {
  reactions: Reaction[]
  self: string
  onPick: (emoji: string) => void
  onClose: () => void
}) {
  const [q, setQ] = useState('')
  const found = useMemo(() => searchEmoji(q), [q])
  const frequent = useMemo(() => [...new Set([...readRecent(), ...QUICK])].slice(0, 14), [])
  const scroller = useRef<HTMLDivElement>(null)
  const [at, setAt] = useState(FREQUENT)
  const mineSet = new Set(reactions.filter((r) => r.users.includes(self)).map((r) => r.emoji))
  const pick = (e: string) => {
    rememberPick(e)
    onPick(e)
    onClose()
  }
  const cell = (e: string) => (
    <button
      key={e}
      type="button"
      className={`rx-cell${mineSet.has(e) ? ' mine' : ''}`}
      aria-label={mineSet.has(e) ? `Remove ${e}` : e}
      aria-pressed={mineSet.has(e)}
      onClick={() => pick(e)}
    >
      {e}
    </button>
  )
  const sections = [{ name: FREQUENT, emoji: frequent }, ...CATEGORIES.map((c) => ({ name: c.name, emoji: c.emoji.map(([e]) => e) }))]
  /** Which kind is at the top of the list now — the foot row marks it. */
  const onScroll = () => {
    const box = scroller.current
    if (!box) return
    let current = FREQUENT
    for (const el of box.querySelectorAll<HTMLElement>('[data-rx-cat]')) {
      if (el.offsetTop - box.offsetTop <= box.scrollTop + 8) current = el.dataset['rxCat'] ?? current
    }
    setAt(current)
  }
  const jump = (name: string) => {
    setQ('')
    setAt(name)
    // After the list is back (a search hides it), bring the kind to the top.
    requestAnimationFrame(() => {
      const el = scroller.current?.querySelector<HTMLElement>(`[data-rx-cat="${name}"]`)
      el?.scrollIntoView?.({ block: 'start' })
    })
  }
  return (
    <Sheet
      title="Add reaction"
      docked
      className="rx-sheet"
      head={
        <label className="rx-search-wrap">
          <Icon name="search" size={20} />
          <input
            className="rx-search"
            type="search"
            placeholder="Search all emoji"
            aria-label="Search all emoji"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
      }
      onClose={onClose}
      actions={
        <nav className="rx-tabs" aria-label="Kinds of emoji">
          {sections.map((s) => (
            <button
              key={s.name}
              type="button"
              className={`rx-tab${at === s.name && !q.trim() ? ' on' : ''}`}
              aria-label={s.name}
              title={s.name}
              onClick={() => jump(s.name)}
            >
              {s.name === FREQUENT ? <Icon name="history" size={20} /> : TAB_GLYPH[s.name] ?? s.emoji[0]}
            </button>
          ))}
        </nav>
      }
    >
      <div className="rx-scroll" ref={scroller} onScroll={onScroll}>
        {q.trim() ? (
          found.length ? (
            <div className="rx-grid">{found.map(cell)}</div>
          ) : (
            <p className="rx-none">No emoji found.</p>
          )
        ) : (
          sections.map((s) => (
            <section key={s.name} className="rx-cat" aria-label={s.name} data-rx-cat={s.name}>
              <h4 className="rx-cat-name">{s.name}</h4>
              <div className="rx-grid">{s.emoji.map(cell)}</div>
            </section>
          ))
        )}
      </div>
    </Sheet>
  )
}

const FREQUENT = 'Frequently Used'
/** The glyph each kind is known by in the foot row — the way Slack's row reads. */
const TAB_GLYPH: Record<string, string> = {
  Smileys: '😀',
  Hands: '👋',
  Hearts: '❤️',
  Celebration: '🎉',
  Symbols: '✅',
  Work: '💻',
  Nature: '🐶',
  Food: '🍔',
}
